// src/components/PostEngagement.js
// Reader engagement under a blog post:
//   • "Was this helpful?" 👍/👎 with live counts (one vote per browser,
//     switchable/undoable — firestore.rules only allows ±1 changes)
//   • optional private feedback note after voting (admin-only postFeedback)
//   • comments — submitted as "pending", shown once approved in the admin,
//     with the author's reply underneath when there is one
import React, { useEffect, useState } from "react";
import { FaThumbsUp, FaThumbsDown, FaSpinner, FaCheck, FaReply } from "react-icons/fa";
import {
  getApprovedComments, addPostComment,
  getPostReactions, changePostReaction, addPostFeedback,
} from "../firebase/dataService";
import "./PostEngagement.css";

const voteKey = (postId) => `post_vote_${postId}`;
const readVote = (postId) => { try { return localStorage.getItem(voteKey(postId)); } catch { return null; } };
const writeVote = (postId, v) => { try { v ? localStorage.setItem(voteKey(postId), v) : localStorage.removeItem(voteKey(postId)); } catch {} };

const formatDate = (ts) => {
  if (!ts) return "";
  try {
    const d = typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch { return ""; }
};

const COMMENT_COOLDOWN_MS = 60 * 1000;

const PostEngagement = ({ post, authorName = "Author" }) => {
  const [counts, setCounts] = useState({ up: 0, down: 0 });
  const [myVote, setMyVote] = useState(() => readVote(post.id)); // "up" | "down" | null
  const [voting, setVoting] = useState(false);

  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [feedbackState, setFeedbackState] = useState("idle"); // idle | sending | sent | error

  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(true);
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [website, setWebsite] = useState(""); // honeypot — real people never see it
  const [commentState, setCommentState] = useState("idle"); // idle | sending | sent | error
  const [commentError, setCommentError] = useState("");

  useEffect(() => {
    getPostReactions(post.id).then(setCounts).catch(() => {});
    getApprovedComments(post.id).then(setComments).catch(() => setComments([])).finally(() => setLoadingComments(false));
  }, [post.id]);

  const vote = async (choice) => {
    if (voting) return;
    const prev = myVote;
    const next = prev === choice ? null : choice; // clicking the same thumb again undoes it
    const delta = {};
    if (prev) delta[prev] = -1;
    if (next) delta[next] = (delta[next] || 0) + 1;
    setVoting(true);
    setMyVote(next);
    setCounts(c => ({ up: Math.max(0, c.up + (delta.up || 0)), down: Math.max(0, c.down + (delta.down || 0)) }));
    try {
      await changePostReaction(post.id, delta);
      writeVote(post.id, next);
      if (next && feedbackState !== "sent") setFeedbackOpen(true);
    } catch {
      setMyVote(prev);
      getPostReactions(post.id).then(setCounts).catch(() => {});
    } finally { setVoting(false); }
  };

  const sendFeedback = async (e) => {
    e.preventDefault();
    if (!feedback.trim()) return;
    setFeedbackState("sending");
    try {
      await addPostFeedback({ post, helpful: myVote ? myVote === "up" : null, message: feedback });
      setFeedbackState("sent"); setFeedback("");
    } catch { setFeedbackState("error"); }
  };

  const sendComment = async (e) => {
    e.preventDefault();
    setCommentError("");
    if (website) { setCommentState("sent"); return; } // bot filled the honeypot — pretend success
    if (!name.trim() || body.trim().length < 2) { setCommentError("Please add your name and a comment."); return; }
    let last = 0;
    try { last = Number(localStorage.getItem("post_comment_last") || 0); } catch {}
    if (Date.now() - last < COMMENT_COOLDOWN_MS) { setCommentError("Please wait a minute before posting another comment."); return; }
    setCommentState("sending");
    try {
      await addPostComment({ post, name, body });
      try { localStorage.setItem("post_comment_last", String(Date.now())); } catch {}
      setCommentState("sent"); setBody("");
    } catch {
      setCommentState("error"); setCommentError("Couldn't send your comment — please try again.");
    }
  };

  const total = counts.up + counts.down;

  return (
    <section className="post-engage" aria-label="Reader feedback and comments">
      {/* ── Helpful? ── */}
      <div className="post-engage__vote card">
        <p className="post-engage__vote-q">Was this post helpful?</p>
        <div className="post-engage__vote-btns">
          <button type="button" className={`post-engage__thumb${myVote === "up" ? " post-engage__thumb--on" : ""}`} onClick={() => vote("up")} disabled={voting} aria-pressed={myVote === "up"}>
            <FaThumbsUp size={14} /> Yes <span>{counts.up}</span>
          </button>
          <button type="button" className={`post-engage__thumb${myVote === "down" ? " post-engage__thumb--on post-engage__thumb--down" : ""}`} onClick={() => vote("down")} disabled={voting} aria-pressed={myVote === "down"}>
            <FaThumbsDown size={14} /> No <span>{counts.down}</span>
          </button>
        </div>
        {total > 0 && <p className="post-engage__vote-meta">{Math.round((counts.up / total) * 100)}% of {total} reader{total !== 1 ? "s" : ""} found this helpful</p>}

        {feedbackOpen && feedbackState !== "sent" && (
          <form className="post-engage__feedback" onSubmit={sendFeedback}>
            <label htmlFor="pe-feedback">{myVote === "down" ? "What could be better? (only I will see this)" : "Anything to add? (optional, only I will see this)"}</label>
            <textarea id="pe-feedback" rows={3} maxLength={2000} value={feedback} onChange={e => setFeedback(e.target.value)} />
            <div className="post-engage__row">
              <button type="button" className="btn btn-ghost" onClick={() => setFeedbackOpen(false)}>No thanks</button>
              <button type="submit" className="btn btn-primary" disabled={!feedback.trim() || feedbackState === "sending"}>
                {feedbackState === "sending" ? <FaSpinner className="spin" size={12} /> : "Send feedback"}
              </button>
            </div>
            {feedbackState === "error" && <p className="post-engage__error">Couldn't send — please try again.</p>}
          </form>
        )}
        {feedbackState === "sent" && <p className="post-engage__ok"><FaCheck size={11} /> Thanks for the feedback!</p>}
      </div>

      {/* ── Comments ── */}
      <div className="post-engage__comments">
        <h2 className="post-engage__title">Comments {comments.length > 0 && <span>({comments.length})</span>}</h2>

        {loadingComments ? (
          <p className="post-engage__muted"><FaSpinner className="spin" size={12} /> Loading comments…</p>
        ) : comments.length === 0 ? (
          <p className="post-engage__muted">No comments yet — be the first.</p>
        ) : (
          <ul className="post-engage__list">
            {comments.map(c => (
              <li key={c.id} className="post-engage__comment">
                <div className="post-engage__comment-head">
                  <span className="post-engage__avatar" aria-hidden="true">{(c.name || "?").charAt(0).toUpperCase()}</span>
                  <strong>{c.name}</strong>
                  <span className="post-engage__muted">{formatDate(c.createdAt)}</span>
                </div>
                <p className="post-engage__comment-body">{c.body}</p>
                {c.reply && (
                  <div className="post-engage__reply">
                    <p className="post-engage__reply-head"><FaReply size={10} /> {authorName} replied</p>
                    <p>{c.reply}</p>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {commentState === "sent" ? (
          <p className="post-engage__ok post-engage__ok--block"><FaCheck size={11} /> Thanks! Your comment will appear once it's been reviewed.</p>
        ) : (
          <form className="post-engage__form" onSubmit={sendComment}>
            <h3>Leave a comment</h3>
            <input className="post-engage__hp" tabIndex={-1} autoComplete="off" aria-hidden="true" value={website} onChange={e => setWebsite(e.target.value)} placeholder="Website" />
            <div className="form-group">
              <label htmlFor="pe-name">Name</label>
              <input id="pe-name" maxLength={60} value={name} onChange={e => setName(e.target.value)} />
            </div>
            <div className="form-group">
              <label htmlFor="pe-body">Comment</label>
              <textarea id="pe-body" rows={4} maxLength={2000} value={body} onChange={e => setBody(e.target.value)} />
            </div>
            {commentError && <p className="post-engage__error">{commentError}</p>}
            <div className="post-engage__row">
              <span className="post-engage__muted">Comments are moderated before they appear.</span>
              <button type="submit" className="btn btn-primary" disabled={commentState === "sending"}>
                {commentState === "sending" ? <><FaSpinner className="spin" size={12} /> Sending…</> : "Post comment"}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};

export default PostEngagement;
